import { computed, ref } from 'vue';
import { UserPreferencesService, type ColorScheme, type ThemePreference } from '@/services/UserPreferencesService';

// Single owner of theme application (PR-770): resolves the stored preference (including
// 'system' via prefers-color-scheme), toggles the root class, keeps the first-paint
// localStorage cache in sync, and follows live OS scheme changes while on 'system'.
// Module-scope state — the theme is global, so every caller shares one instance. The
// MediaQueryList initializes lazily on first use so tests can stub matchMedia first.

const themePreference = ref<ThemePreference>('light');
const osPrefersDark = ref(false);
let mediaInitialized = false;

function ensureMedia() {
  if (mediaInitialized) return;
  mediaInitialized = true;
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  osPrefersDark.value = media.matches;
  media.addEventListener('change', (event) => {
    osPrefersDark.value = event.matches;
    if (themePreference.value === 'system') {
      applyResolved(event.matches ? 'dark' : 'light');
    }
  });
}

export function resolveColorScheme(pref: ThemePreference): ColorScheme {
  ensureMedia();
  return pref === 'system' ? (osPrefersDark.value ? 'dark' : 'light') : pref;
}

function applyResolved(scheme: ColorScheme) {
  document.documentElement.classList.toggle('my-app-dark', scheme === 'dark');
}

export function useTheme() {
  ensureMedia();

  const isDarkResolved = computed(() => resolveColorScheme(themePreference.value) === 'dark');

  /** Adopt the already-persisted preference without re-saving (bootstrap/mount/logout sync). */
  function syncFromPreferences() {
    themePreference.value = UserPreferencesService.getColorScheme() ?? 'system';
    applyResolved(resolveColorScheme(themePreference.value));
  }

  function setThemePreference(pref: ThemePreference) {
    themePreference.value = pref;
    applyResolved(resolveColorScheme(pref));
    // First-paint cache for App.vue; stores the raw preference — 'system' resolves there too.
    window.localStorage.setItem('theme', pref);
    UserPreferencesService.setColorScheme(pref);
  }

  /** Header shortcut: flips to the explicit opposite of the current resolved scheme. */
  function toggleTheme() {
    setThemePreference(isDarkResolved.value ? 'light' : 'dark');
  }

  return { themePreference, isDarkResolved, syncFromPreferences, setThemePreference, toggleTheme };
}
